import { useEffect, useRef } from 'react';

// Lightweight parallax utility (scoped version from Account.tsx)
type Axis = 'y' | 'x' | 'both';
interface ParallaxOptions {
  speed?: number;
  axis?: Axis;
  maxTranslate?: number;
  rootMargin?: string;
}
interface ParallaxItem {
  element: HTMLElement;
  speed: number;
  axis: Axis;
  maxTranslate: number;
  inView: boolean;
  lastTransform: string;
}

const parallaxItems = new Set<ParallaxItem>();
let parallaxIo: IntersectionObserver | null = null;
let parallaxRafId: number | null = null;
let parallaxRunning = false;
let prefersReducedMotion =
  typeof window !== 'undefined' &&
  !!window.matchMedia &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

function ensureParallaxIO(rootMargin: string) {
  if (parallaxIo) return;
  parallaxIo = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        const item = [...parallaxItems].find((it) => it.element === entry.target);
        if (!item) continue;
        item.inView = entry.isIntersecting;
      }
      requestParallaxTick();
    },
    { root: null, threshold: 0, rootMargin },
  );
}
function onParallaxScroll() {
  requestParallaxTick();
}
function onParallaxResize() {
  requestParallaxTick();
}
function startParallax() {
  if (parallaxRunning) return;
  parallaxRunning = true;
  window.addEventListener('scroll', onParallaxScroll, { passive: true });
  window.addEventListener('resize', onParallaxResize, { passive: true });
  requestParallaxTick();
}
function stopParallax() {
  if (!parallaxRunning) return;
  window.removeEventListener('scroll', onParallaxScroll);
  window.removeEventListener('resize', onParallaxResize);
  parallaxRunning = false;
}
function requestParallaxTick() {
  if (parallaxRafId !== null) return;
  parallaxRafId = window.requestAnimationFrame(updateParallax);
}
function clamp(val: number, min: number, max: number) {
  return Math.min(Math.max(val, min), max);
}
function updateParallax() {
  parallaxRafId = null;
  if (prefersReducedMotion) {
    parallaxItems.forEach((item) => {
      if (item.lastTransform !== '') {
        item.element.style.transform = '';
        item.lastTransform = '';
      }
    });
    return;
  }
  const viewportHeight = window.innerHeight || 0;
  const viewportWidth = window.innerWidth || 0;
  const centerY = viewportHeight / 2;
  const centerX = viewportWidth / 2;
  const farTop = -viewportHeight * 1.5;
  const farBottom = viewportHeight * 2.5;
  parallaxItems.forEach((item) => {
    const rect = item.element.getBoundingClientRect();
    if (rect.bottom < farTop || rect.top > farBottom) return;
    const elementCenterY = rect.top + rect.height / 2;
    const elementCenterX = rect.left + rect.width / 2;
    const dy = elementCenterY - centerY;
    const dx = elementCenterX - centerX;
    const translateY = clamp(-dy * item.speed, -item.maxTranslate, item.maxTranslate);
    const translateX = clamp(-dx * item.speed, -item.maxTranslate, item.maxTranslate);
    let transform = '';
    if (item.axis === 'y') {
      transform = `translate3d(0, ${translateY.toFixed(2)}px, 0)`;
    } else if (item.axis === 'x') {
      transform = `translate3d(${translateX.toFixed(2)}px, 0, 0)`;
    } else {
      transform = `translate3d(${translateX.toFixed(2)}px, ${translateY.toFixed(2)}px, 0)`;
    }
    if (transform !== item.lastTransform) {
      item.element.style.transform = transform;
      item.lastTransform = transform;
    }
  });
}
export function useParallaxRef(options: ParallaxOptions = {}) {
  const {
    speed = 0.06,
    axis = 'y',
    maxTranslate = 40,
    rootMargin = '20% 0px 20% 0px',
  } = options;
  const elRef = useRef<HTMLElement | null>(null);
  useEffect(() => {
    const el = elRef.current as HTMLElement | null;
    if (!el) return;
    const mql = window.matchMedia('(prefers-reduced-motion: reduce)');
    const onChange = () => {
      prefersReducedMotion = mql.matches;
      requestParallaxTick();
    };
    mql.addEventListener?.('change', onChange);
    ensureParallaxIO(rootMargin);
    startParallax();
    const item: ParallaxItem = {
      element: el,
      speed,
      axis,
      maxTranslate,
      inView: false,
      lastTransform: '',
    };
    try {
      const rect = el.getBoundingClientRect();
      const vh = window.innerHeight || 0;
      const vm = Math.max(0, Math.min(0.5, parseFloat(rootMargin.split(' ')[0]) / 100 || 0.2));
      const extendedTop = -vh * vm;
      const extendedBottom = vh * (1 + vm);
      item.inView = rect.bottom >= extendedTop && rect.top <= extendedBottom;
    } catch {
      item.inView = true;
    }
    parallaxItems.add(item);
    parallaxIo?.observe(el);
    el.style.willChange = 'transform';
    el.style.backfaceVisibility = 'hidden';
    el.style.transformStyle = 'preserve-3d';
    updateParallax();
    return () => {
      mql.removeEventListener?.('change', onChange);
      parallaxIo?.unobserve(el);
      parallaxItems.delete(item);
      el.style.willChange = '';
      el.style.backfaceVisibility = '';
      el.style.transformStyle = '';
      el.style.transform = '';
      if (parallaxItems.size === 0) {
        stopParallax();
      }
    };
  }, [axis, maxTranslate, rootMargin, speed]);
  return elRef;
}

